/**
 * SearchableSelect Component
 * Searchable dropdown with filtering
 * Supports both single and multi-select modes
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  id?: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  className?: string;
  // Multi-select props
  multiple?: boolean;
  values?: string[];
  onMultiChange?: (values: string[]) => void;
}

export const SearchableSelect = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Search...',
  error,
  helperText,
  disabled = false,
  className,
  multiple = false,
  values = [],
  onMultiChange,
}: SearchableSelectProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (query === '') return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    );
  }, [options, query]);

  // Sort options: selected first (for multi-select mode)
  const sortedOptions = useMemo(() => {
    if (!multiple) return filteredOptions;

    const selected = filteredOptions.filter((o) => values.includes(o.value));
    const unselected = filteredOptions.filter((o) => !values.includes(o.value));
    return [...selected, ...unselected];
  }, [filteredOptions, values, multiple]);

  // Get display value for input
  const getDisplayValue = (): string => {
    if (multiple) {
      if (values.includes('all')) return 'All Teams';
      if (values.length === 0) return '';
      if (values.length === 1) {
        const option = options.find((o) => o.value === values[0]);
        return option?.label || '';
      }
      return `${values.length} Teams Selected`;
    }
    return options.find((option) => option.value === value)?.label || '';
  };

  // Handle multi-selection
  const handleMultiSelect = (optionValue: string) => {
    if (optionValue === 'all') {
      // Selecting "All Teams" clears everything and selects only 'all'
      onMultiChange?.(['all']);
    } else {
      // Selecting a specific team
      let newValues = values.filter((v) => v !== 'all');
      if (newValues.includes(optionValue)) {
        // Deselect
        newValues = newValues.filter((v) => v !== optionValue);
        if (newValues.length === 0) {
          newValues = ['all']; // Default back to all
        }
      } else {
        // Select
        newValues = [...newValues, optionValue];
      }
      onMultiChange?.(newValues);
    }
    // Keep dropdown open and clear search query
    setQuery('');
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (multiple) {
    // Multi-select mode - custom implementation
    return (
      <div className={cn('w-full', className)} ref={containerRef}>
        {label && (
          <label className="block text-sm font-medium text-body mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          <div className="relative w-full">
            <input
              ref={inputRef}
              id={selectId}
              type="text"
              className={cn(
                'block w-full rounded-md border-strong shadow-sm focus:border-focus focus:ring-blue-500 text-sm text-heading bg-surface',
                'px-3 py-2 pr-10 border',
                error && 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500',
                disabled && 'bg-surface-secondary cursor-not-allowed'
              )}
              value={isOpen ? query : getDisplayValue()}
              onChange={(e) => {
                setQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
                setQuery('');
              }}
              placeholder={isOpen ? placeholder : getDisplayValue() || placeholder}
              disabled={disabled}
              autoComplete="off"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => {
                if (!disabled) {
                  setIsOpen(!isOpen);
                  if (!isOpen) {
                    inputRef.current?.focus();
                  }
                }
              }}
              tabIndex={-1}
              aria-label={`${isOpen ? 'Close' : 'Open'} ${label ? `${label} ` : ''}options`}
              aria-expanded={isOpen}
            >
              <ChevronDown
                className={cn('h-4 w-4 text-meta transition-transform', isOpen && 'rotate-180')}
                aria-hidden="true"
              />
            </button>
          </div>

          {isOpen && (
            <div
              className={cn(
                'absolute z-10 mt-1 w-full rounded-md bg-surface shadow-lg',
                'border border-strong',
                'max-h-[280px] overflow-y-auto',
                'py-1 text-sm'
              )}
            >
              {sortedOptions.length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-body">
                  No teams found
                </div>
              ) : (
                sortedOptions.map((option) => {
                  const isSelected = values.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      className={cn(
                        'relative cursor-pointer select-none py-2 pl-3 pr-9 flex items-center',
                        'text-heading hover:bg-surface-hover',
                        isSelected && 'bg-blue-50 dark:bg-blue-900/20',
                        option.value === 'all' && 'border-b border-[var(--border-default)]'
                      )}
                      onClick={() => handleMultiSelect(option.value)}
                      onMouseDown={(e) => e.preventDefault()} // Prevent blur
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2 pointer-events-none"
                      />
                      <span
                        className={cn(
                          'block truncate',
                          isSelected && 'font-semibold'
                        )}
                        title={option.label}
                      >
                        {option.label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-meta">{helperText}</p>
        )}
      </div>
    );
  }

  // Single-select mode - custom implementation
  const handleSingleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setQuery('');
    setIsOpen(false);
    setTimeout(() => inputRef.current?.blur(), 0);
  };

  return (
    <div className={cn('w-full', className)} ref={containerRef}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-body mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <div className="relative w-full">
          <input
            ref={inputRef}
            id={selectId}
            type="text"
            className={cn(
              'block w-full rounded-md border-strong shadow-sm focus:border-focus focus:ring-blue-500 text-sm text-heading bg-surface',
              'px-3 py-2 pr-10 border',
              error && 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500',
              disabled && 'bg-surface-secondary cursor-not-allowed'
            )}
            value={isOpen ? query : getDisplayValue()}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => {
              setIsOpen(true);
              setQuery('');
            }}
            placeholder={isOpen ? placeholder : getDisplayValue() || placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen);
                if (!isOpen) {
                  inputRef.current?.focus();
                }
              }
            }}
            tabIndex={-1}
            aria-label={`${isOpen ? 'Close' : 'Open'} ${label ? `${label} ` : ''}options`}
            aria-expanded={isOpen}
          >
            <ChevronDown
              className={cn('h-4 w-4 text-meta transition-transform', isOpen && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        </div>

        {isOpen && (
          <div
            className={cn(
              'absolute z-10 mt-1 w-full rounded-md bg-surface shadow-lg',
              'border border-strong',
              'max-h-[280px] overflow-y-auto',
              'py-1 text-sm'
            )}
          >
            {filteredOptions.length === 0 ? (
              <div className="relative cursor-default select-none px-4 py-2 text-body">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <div
                    key={option.value}
                    className={cn(
                      'relative cursor-pointer select-none py-2 pl-3 pr-9',
                      'text-heading hover:bg-surface-hover',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                    )}
                    onClick={() => handleSingleSelect(option.value)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <span
                      className={cn(
                        'block truncate',
                        isSelected && 'font-semibold'
                      )}
                      title={option.label}
                    >
                      {option.label}
                    </span>
                    {isSelected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-meta">{helperText}</p>
      )}
    </div>
  );
};

SearchableSelect.displayName = 'SearchableSelect';
